import { useEffect, useReducer, useRef } from "react"
import "lib/@hooks/useEvent"
import { getScheduleEditorDraftAmends } from "authoring/routes/project/schedule-editor/controller/get-schedule-editor-draft-amends"
import { upsertDraftAmends } from "authoring/routes/project/schedule-editor/controller/upsert-draft-amends"
import { deleteAuthoringAmends } from "authoring/routes/project/amends/controller/delete-authoring-amends"
import { stripChipHtml } from "authoring/routes/macro/lib/macro-chip-utils"
import { stripBlacklistedFontStyles } from "authoring/routes/pdf/components/strip-blacklisted-font-styles"
import { normaliseAmendsContentForSave } from "authoring/routes/project/amends/lib/amends-content"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { SeedSource } from "authoring/lib/enum/seed-source.mjs"
import { createDraftId } from "authoring/routes/project/lib/project-utils"
import { AmendsChanged } from "../schedule-editor-event-definitions.mjs"
import { buildMergedAmends } from "./build-merged-amends"

export function useDraftAmends({ projectId, scheduleId, flushDraftBeforeSave, seedSource } = {}) {
    const isNewSchedule = seedSource === SeedSource.NEW
    const editorAmends = getScheduleEditorDraftAmends.useResultsOnce(
        { projectId, scheduleId },
        withInitialValue(IS_LOADING)
    )
    const initialLoading = editorAmends === IS_LOADING

    const data = initialLoading ? null : editorAmends ?? {}
    const persistedAmends = data?.persistedAmends ?? []
    const inheritedAmends = data?.inheritedAmends ?? []
    const draftId = createDraftId(projectId, scheduleId)

    const retainedInheritedAmends = isNewSchedule ? inheritedAmends.filter(shouldKeepAuthorNotesAmend) : inheritedAmends
    const amends = buildMergedAmends({ persistedAmends, inheritedAmends: retainedInheritedAmends })

    const readyRaisedRef = useRef(false)
    const [, forceUpdate] = useReducer(tick, 0)
    useEffect(notifyAmendsReady, [draftId, initialLoading])
    AmendsChanged(draftId).useEvent(handleAmendsChangedEvent, [draftId, projectId, scheduleId])

    return {
        amends,
        persistedAmends,
        inheritedAmends,
        loading: initialLoading,
        initialLoading,
        saveAmend,
        deleteAmend,
        refreshAmends: mergeEditorAmendsFromServer,
    }

    function notifyAmendsReady() {
        if (initialLoading || readyRaisedRef.current || !draftId) return
        readyRaisedRef.current = true
        AmendsChanged(draftId).raise()
    }

    async function saveAmend({
        amendsKey,
        content,
        internalContent,
        externalContent,
        noteMode,
        recommendationId,
        sourceType,
        sourceRecordId,
        sourceLabel,
        sourceRoute,
        silent = false,
    }) {
        if (!projectId || !scheduleId || !amendsKey) return null

        try {
            if (typeof flushDraftBeforeSave === "function") {
                await flushDraftBeforeSave()
            }
            const saved = await upsertDraftAmends({
                projectId,
                scheduleId,
                amends: {
                    amendsKey,
                    content: normaliseAmendsContentForSave(stripBlacklistedFontStyles(stripChipHtml(content))),
                    internalContent: normaliseAmendsContentForSave(
                        stripBlacklistedFontStyles(stripChipHtml(internalContent))
                    ),
                    externalContent: normaliseAmendsContentForSave(
                        stripBlacklistedFontStyles(stripChipHtml(externalContent))
                    ),
                    noteMode,
                    recommendationId,
                    sourceType,
                    sourceRecordId,
                    sourceLabel,
                    sourceRoute,
                },
            })
            if (!silent) {
                successSnackbar("Author notes saved")
            }
            if (saved) {
                mergeOrAppendAmendInPlace(persistedAmends, saved)
                forceUpdate()
                // AmendsChanged is raised centrally by the upsertDraftAmends controller
            }
            return saved
        } catch (error) {
            if (!silent) {
                errorSnackbar("Failed to save author notes")
            }
            throw error
        }
    }

    async function deleteAmend({ id, silent = false } = {}) {
        if (!projectId || !id) return null

        try {
            const deleted = await deleteAuthoringAmends({
                projectId,
                amendsId: id,
            })
            if (!silent) {
                successSnackbar("Author notes deleted")
            }
            if (deleted) {
                const index = persistedAmends.findIndex((amend) => amend?.id === id)
                if (index >= 0) {
                    persistedAmends.splice(index, 1)
                    forceUpdate()
                    AmendsChanged(draftId).raise()
                }
            }
            return deleted
        } catch (error) {
            if (!silent) {
                errorSnackbar("Failed to delete author notes")
            }
            throw error
        }
    }

    function handleAmendsChangedEvent(payload) {
        if (payload?.source !== "comparison-sync") return
        void mergeEditorAmendsFromServer()
    }

    async function mergeEditorAmendsFromServer() {
        if (!projectId || !scheduleId || !draftId) return null

        const fresh = await getScheduleEditorDraftAmends({ projectId, scheduleId })
        if (!fresh) return null

        replaceAmendsInPlace(persistedAmends, fresh.persistedAmends ?? [])
        replaceAmendsInPlace(inheritedAmends, fresh.inheritedAmends ?? [])
        forceUpdate()
        AmendsChanged(draftId).raise()
        return fresh
    }
}

function tick(value) {
    return value + 1
}

function shouldKeepAuthorNotesAmend(amend) {
    return !amend?.payload?.status
}

function mergeOrAppendAmendInPlace(target, saved) {
    const index = target.findIndex((amend) => amend?.id === saved.id)
    if (index < 0) {
        target.push(saved)
        return
    }
    target[index] = saved
}

function replaceAmendsInPlace(target, source) {
    target.splice(0, target.length, ...(Array.isArray(source) ? source : []))
}
